import { useState } from 'react';
import { useAction } from 'convex/react';
import { Calculator, Loader2, X } from 'lucide-react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import { Button, Callout } from '@bill-alps/ui';
import { isAffectedByAccountingBilling } from '@bill-alps/backend/features/accountingNotice/rules';
import { getBilledModules } from '@bill-alps/backend/shared/billing/moduleEntitlements';
import { normalizeModuleSet } from '@bill-alps/backend/shared/billing/plans';
import { useTranslation } from '../i18n/useTranslation';
import { useCurrentUser } from '@/hooks/use-current-user';
import { IS_SANDBOX } from '@/lib/runtime/feature-flags';
import { isPaidPlan, priceFor } from '@/lib/billing/billing-package';
import { formatChf } from './plan-picker';

const DISMISSED_KEY = 'bill-alps:accounting-billing-notice-dismissed';

/**
 * « Ajouter Comptabilité » — shown to a paying company that uses expenses or
 * the bank import without the accounting line in its package: those run at
 * Starter limits now (see docs/PRICING.md, no grandfathering). The CTA opens
 * Stripe's confirm-update step on the current package + accounting, same
 * size, same interval. Dismissible per company for the session; the 30-day
 * e-mail (`sendAccountingNoticeEmails`) is the durable notice.
 */
export function AccountingBillingBanner() {
  const { t } = useTranslation('subscriptionBanner');
  const { user, isFiduciaire } = useCurrentUser();
  const createUpdateFlow = useAction(
    api.features.stripe.subscriptions.createSubscriptionUpdateFlow,
  );
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Read once on mount: the only writer is the dismiss button below.
  const [dismissed, setDismissed] = useState<string | null>(() =>
    typeof window === 'undefined'
      ? null
      : window.sessionStorage.getItem(DISMISSED_KEY),
  );

  const company = user?.company;
  const companyId = company?._id;

  if (IS_SANDBOX || isFiduciaire || !company || !companyId) return null;
  if (!isPaidPlan(company.plan) || !isAffectedByAccountingBilling(company)) {
    return null;
  }
  if (dismissed === companyId) return null;

  const plan = company.plan;
  const current = getBilledModules(company);
  const next = normalizeModuleSet([...current, 'accounting']);
  const interval = company.billingInterval ?? 'month';
  const extra =
    (priceFor(plan, next, interval) - priceFor(plan, current, interval)) /
    (interval === 'year' ? 12 : 1);
  const isOwner = user?._id === company.userId;

  const handleAdd = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const { url } = await createUpdateFlow({
        companyId,
        plan,
        modules: next,
        interval,
      });
      if (url) window.location.href = url;
      else setLoading(false);
    } catch {
      // Inline rather than a toast: `<Toaster />` only exists inside the app
      // shell, and this bar also renders above it.
      setFailed(true);
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    window.sessionStorage.setItem(DISMISSED_KEY, companyId);
    setDismissed(companyId);
  };

  return (
    <Callout
      variant="info"
      size="sm"
      icon={<Calculator />}
      role="status"
      data-testid="accounting-billing-banner"
      className="m-2"
      actions={
        <>
          {isOwner && (
            <Button
              variant="accent"
              size="sm"
              onClick={() => void handleAdd()}
              disabled={loading}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('addAccounting')}
            </Button>
          )}
          {failed && (
            <span role="alert" className="text-destructive">
              {t('addAccountingError')}
            </span>
          )}
          <button
            type="button"
            onClick={handleDismiss}
            className="ml-1 cursor-pointer rounded p-1 text-muted-foreground hover:text-foreground"
            aria-label={t('dismiss')}
          >
            <X className="h-4 w-4" />
          </button>
        </>
      }
    >
      {t('accountingStarterLimits', { price: formatChf(extra) })}
    </Callout>
  );
}
